import type {
  GetFoodImageResponse,
  FoodSearchFood,
} from "@tandemdiabetes/sugarmate-api-react-client"
import { useGetFoodImage } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  useCallback,
  useEffect,
  useState,
  useMemo,
} from "react"
import { useIsMounted } from "usehooks-ts"
import { noticeError } from "src/utils"

let cachedResponses: Record<string, GetFoodImageResponse> = {}

interface ReturnValue {
  response: GetFoodImageResponse | null;
  clearCache: () => void;
}

export function useFoodImage(food: FoodSearchFood | null): ReturnValue {
  const getFoodImage = useGetFoodImage()
  const isMounted = useIsMounted()

  const [
    imageResponse,
    setImageResponse,
  ] = useState<GetFoodImageResponse | null>(null)

  const clearCache = useCallback(
    () => {
      cachedResponses = {}
    },
    [],
  )

  const requestImage = useCallback(
    async (query: string) => {
      // use the cached response if there is one
      if (cachedResponses[query]) {
        setImageResponse(cachedResponses[query])
      } else {
        // hit the endpoint
        try {
          if (!getFoodImage) {
            throw new Error("cannot get food image for unauthorized user")
          }

          const response = await getFoodImage(query)

          if (isMounted()) {
            // update state w/ the  response
            setImageResponse(response.data)

            // cache the response
            cachedResponses[query] = response.data
          }
        } catch (e) {
          noticeError(
            e,
            "SearchFoodImage",
            { query },
          )
        }
      }
    },
    [
      getFoodImage,
      isMounted,
      setImageResponse,
    ],
  )

  // invoke the get function every time the foodName changes
  useEffect(
    () => {
      if (food !== null) {
        const query = food.brand
          ? `${food.brand} ${food.name}`.trim()
          : food.name.trim()


        if (query) {
          void requestImage(query)
          return
        }
      }

      setImageResponse(null)
    },
    [
      setImageResponse,
      requestImage,
      food,
    ],
  )

  return useMemo(
    () => ({
      response: imageResponse,
      clearCache,
    }),
    [
      imageResponse,
      clearCache,
    ],
  )
}
